<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-surface-50">
    <Loader v-if="isLoading" class="self-center my-auto" />
    <div v-else class="flex flex-col w-full md:max-w-[560px] px-3.5 pb-6 pt-3">
      <!-- Clearing status -->
      <settings-card>
        <div v-if="isConnected" class="flex items-center gap-3 py-3" :class="hasPaymeInvoice ? 'border-b border-surface-100' : ''">
          <div class="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center flex-none">
            <i class="pi pi-check text-base text-green-700" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium">סליקת אשראי</div>
            <div class="text-xs text-surface-500 mt-0.5">מחובר{{ providerLabel ? ` · ${providerLabel}` : '' }}</div>
          </div>
          <span class="text-[10.5px] font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full flex-none">פעיל</span>
        </div>
        <div v-else class="flex items-center gap-3 py-3" :class="hasPaymeInvoice ? 'border-b border-surface-100' : ''">
          <div class="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center flex-none">
            <i class="pi pi-credit-card text-base text-surface-500" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium">סליקת אשראי</div>
            <div class="text-xs text-surface-500 mt-0.5">לא מחוברת — חברו סליקה כדי לגבות תשלומים באתר</div>
          </div>
          <Button label="חיבור" size="small" @click="goToIntegrations" />
        </div>
        <settings-toggle-row
          v-if="hasPaymeInvoice"
          title="חשבונית אוטומטית"
          description="חשבונית תישלח ללקוח אוטומטית אחרי כל תשלום"
          :status="invoiceAutomationEnabled"
          last
          @value-changed="toggleInvoiceAutomation"
        />
      </settings-card>

      <!-- Charging at booking requires a clearing provider — an invoice provider alone
           can't collect payment, so don't show the payment-mode options without it. -->
      <template v-if="siteData.hasPayments">
        <!-- Manual approve conflict warning -->
        <div
          v-if="siteData.configurations.appointmentManualApprove"
          class="mt-3 bg-yellow-50 border border-yellow-200 rounded-2xl px-3.5 py-3 flex items-center gap-2.5"
        >
          <i class="pi pi-exclamation-triangle text-yellow-600 flex-none" />
          <span class="text-xs text-yellow-800">{{ hasPayme
            ? 'כאשר אישור ידני של תורים מופעל, תשלום חובה מתבצע בשמירת כרטיס וחיוב לאחר אישור התור.'
            : 'כאשר אישור ידני של תורים מופעל לא ניתן לחייב תשלום חובה — התשלום יהיה אופציונלי.' }}</span>
        </div>

        <settings-group-label>תשלום בעת קביעת תור</settings-group-label>
        <settings-card>
          <settings-radio-group
            :modelValue="paymentSettings.schedulePaymentMode"
            :disabled="loadingCta"
            last
            :options="paymentModeOptions"
            @update:modelValue="onPaymentModeChange"
          />
        </settings-card>

        <!-- Info for optional mode -->
        <div
          v-if="paymentSettings.schedulePaymentMode === 'optional'"
          class="mt-3 bg-blue-50 border border-blue-200 rounded-2xl px-3.5 py-3 flex items-start gap-2.5"
        >
          <i class="pi pi-info-circle text-blue-600 flex-none mt-0.5" />
          <span class="text-xs text-surface-600">במצב תשלום אופציונלי, אם הלקוח בחר לשלם — הוא ישלם את <strong>המחיר המלא</strong> (פחות הנחות שוברים/נקודות)</span>
        </div>

        <template v-if="paymentSettings.schedulePaymentMode === 'force'">
          <!-- Amount (hidden when save-card timing) -->
          <template v-if="!isSaveCardTiming">
            <settings-group-label>סכום החיוב</settings-group-label>
            <settings-card>
              <settings-radio-group
                :modelValue="amountChoice"
                :disabled="loadingCta"
                :last="amountChoice !== 'partial'"
                :options="[
                  { value: 'full', label: 'תשלום מלא', desc: 'הלקוח משלם את מלוא הסכום בעת קביעת התור' },
                  { value: 'partial', label: 'מקדמה (תשלום חלקי)', desc: 'הלקוח משלם אחוז מהסכום, והיתרה משולמת בעסק' },
                ]"
                @update:modelValue="(v) => { amountChoice = v; onAmountChoiceChanged(v); }"
              />
              <div v-if="amountChoice === 'partial'" class="py-3">
                <label class="block text-[11px] font-semibold text-surface-500 mb-1.5">אחוז מקדמה (%)</label>
                <InputNumber
                  v-model="paymentSettings.scheduleAdvancedPaymentPercentage"
                  :min="1"
                  :max="99"
                  :disabled="loadingCta"
                  placeholder="50"
                  suffix="%"
                  class="w-full"
                />
              </div>
            </settings-card>
          </template>

          <!-- Charge timing (PayMe only) -->
          <template v-if="hasPayme">
            <settings-group-label>מועד החיוב</settings-group-label>
            <settings-card>
              <settings-radio-group
                :modelValue="paymentSettings.scheduleChargeMode"
                :disabled="loadingCta"
                last
                :options="chargeModeOptions"
                @update:modelValue="(v) => paymentSettings.scheduleChargeMode = v"
              />
              <div v-if="paymentSettings.scheduleChargeMode === 'before-appointment'" class="pb-3">
                <div class="flex items-center gap-3 bg-surface-50 rounded-xl px-3 py-2.5">
                  <span class="flex-1 text-xs text-surface-600">כמה שעות לפני התור לחייב?</span>
                  <settings-stepper
                    :modelValue="paymentSettings.scheduleChargeHoursBefore ?? 2"
                    :min="1"
                    :max="168"
                    :disabled="loadingCta"
                    suffix="שעות"
                    @update:modelValue="(v) => paymentSettings.scheduleChargeHoursBefore = v"
                  />
                </div>
                <div class="text-[11px] text-surface-500 leading-relaxed mt-2 px-1">
                  <i class="pi pi-info-circle text-blue-600 text-xs ml-1" />
                  אם הלקוח מבטל בזמן, הכרטיס לא יחויב. תורים שנקבעים סמוך למועד יחויבו מיידית.
                </div>
              </div>
              <div v-if="paymentSettings.scheduleChargeMode === 'save-card'" class="pb-3">
                <div class="text-[11px] text-surface-500 leading-relaxed px-1">
                  <i class="pi pi-info-circle text-blue-600 text-xs ml-1" />
                  הכרטיס נשמר עד 7 ימים אחרי מועד התור. ניתן לחייב מלא או חלקי דרך אפשרויות התשלום של התור.
                </div>
              </div>
            </settings-card>
          </template>

          <!-- Who pays -->
          <settings-group-label>מי מחויב</settings-group-label>
          <settings-card>
            <settings-radio-group
              :modelValue="paymentSettings.scheduleChargeScope"
              :disabled="loadingCta"
              last
              :options="[
                { value: 'always', label: 'בכל קביעת תור', desc: 'כל הלקוחות משלמים בכל קביעת תור' },
                { value: 'first-booking', label: 'רק בתור הראשון (לקוחות חדשים)', desc: 'לקוחות חוזרים יקבעו תור ללא תשלום' },
              ]"
              @update:modelValue="(v) => paymentSettings.scheduleChargeScope = v"
            />
          </settings-card>
        </template>

        <settings-group-label>שליחת קישורי תשלום</settings-group-label>
        <settings-card>
          <settings-radio-group
            :modelValue="paymentSettings.paymentLinkChannel"
            :disabled="loadingCta"
            last
            :options="[
              { value: 'sms', label: 'SMS', desc: 'קישור התשלום (כולל בקשות ביט וחיוב שנכשל) נשלח ללקוח בהודעת SMS' },
              { value: 'whatsapp', label: 'WhatsApp', desc: whatsappChannelDesc },
            ]"
            @update:modelValue="(v) => paymentSettings.paymentLinkChannel = v"
          />
        </settings-card>

        <settings-group-label>שליחת חשבוניות</settings-group-label>
        <settings-card>
          <settings-radio-group
            :modelValue="paymentSettings.invoiceLinkChannel"
            :disabled="loadingCta"
            last
            :options="[
              { value: 'sms', label: 'SMS', desc: 'קישור לחשבונית נשלח ללקוח בהודעת SMS' },
              { value: 'whatsapp', label: 'WhatsApp', desc: whatsappMessageDesc('עם כפתור לצפייה בחשבונית') },
            ]"
            @update:modelValue="(v) => paymentSettings.invoiceLinkChannel = v"
          />
        </settings-card>
        <div class="text-xs text-surface-500 px-1 -mt-1 mb-3">
          חל על שליחה ידנית מהתור ועל חשבוניות EZcount. חשבוניות PayMe ו-SmartBee נשלחות לפי אוטומציית "שליחת חשבונית".
        </div>

        <settings-group-label>מדיניות ותנאים</settings-group-label>
        <settings-card>
          <settings-toggle-row
            title="פיצול לתשלומים"
            description="האם לאפשר פיצול תשלום לתשלומים?"
            :status="paymentSettings.allowTashlumim ?? false"
            last
            @value-changed="(val) => paymentSettings.allowTashlumim = val"
          >
            <Select
              v-model="paymentSettings.numOfTashlumim"
              placeholder="כמה תשלומים?"
              class="w-full"
              :options="[
                { name: '2 תשלומים', value: 2 },
                { name: '3 תשלומים', value: 3 },
                { name: '4 תשלומים', value: 4 },
                { name: '5 תשלומים', value: 5 }
              ]"
              optionLabel="name"
              optionValue="value"
            />
          </settings-toggle-row>
        </settings-card>

      </template>

      <!-- Owners issue gift vouchers by hand without a clearing provider too -->
      <settings-group-label>שליחת שוברי מתנה</settings-group-label>
      <settings-card>
        <settings-radio-group
          :modelValue="paymentSettings.voucherChannel"
          :disabled="loadingCta"
          last
          :options="[
            { value: 'sms', label: 'SMS', desc: 'השובר נשלח למקבל/ת בהודעת SMS' },
            { value: 'whatsapp', label: 'WhatsApp', desc: whatsappMessageDesc('עם כפתור לצפייה בשובר') },
          ]"
          @update:modelValue="(v) => paymentSettings.voucherChannel = v"
        />
      </settings-card>

      <settings-save-bar v-if="canEdit" :dirty="dirty" :saved="saved" :loading="loadingCta" @save="saveChanges" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAdminStore } from '@app/stores';
import { computed, reactive, ref } from 'vue';
import {
  AutomationTypeEnum,
  IAutomationConfig,
  InvoiceProvider,
  PaymentProvider,
  TSettingsPayments
} from '@torim-app/types';
import { CommunicationApi, SettingsApi } from '@app/api/admin';
import { useToast } from 'vue-toastification';
import Select from 'primevue/select';
import { useRouter } from 'vue-router';
import { useDrawer } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';
import { AdminRouteNames } from '@app/router/route-names';
import { automationTypesMeta, createDefaultConfig } from '../communication/automation-types-config';
import EditAutomationDrawer from '../communication/EditAutomationDrawer.vue';
import SettingsCard from './ui/SettingsCard.vue';
import SettingsGroupLabel from './ui/SettingsGroupLabel.vue';
import SettingsToggleRow from './ui/SettingsToggleRow.vue';
import SettingsRadioGroup from './ui/SettingsRadioGroup.vue';
import SettingsStepper from './ui/SettingsStepper.vue';
import SettingsSaveBar from './ui/SettingsSaveBar.vue';
import { useSettingsForm } from './ui/settings-form';

const adminStore = useAdminStore();
const toast = useToast();
const router = useRouter();

const loadingCta = ref(false);
const paymentSettings: TSettingsPayments = reactive<TSettingsPayments>(<TSettingsPayments>{});

const amountChoice = ref<'full' | 'partial'>('full');

const { dirty, saved, takeSnapshot, flashSaved } = useSettingsForm(() => ({ paymentSettings, amountChoice: amountChoice.value }));

const isLoading = computed(() => adminStore.getFetchingInProgress);
const siteData = computed(() => adminStore.getBusinessSiteData);
const canEdit = computed(() => adminStore.getCanEdit);
const hasPayme = computed(() => siteData.value?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.PAYME);
// WhatsApp payment links go out from the business's own number when connected, else from
// the TimeIt number; undelivered ones fall back to SMS automatically.
const whatsappMessageDesc = (what: string) =>
  (siteData.value?.whatsappIntegration?.phoneNumberId
    ? `הודעת WhatsApp מהמספר העסקי שלכם ${what}`
    : `הודעת WhatsApp ממספר TimeIt ${what}`)
  + ' · אם ההודעה לא נמסרת, נשלח SMS במקומה';
const whatsappChannelDesc = computed(() => whatsappMessageDesc('עם כפתור לתשלום'));

const isSaveCardTiming = computed(() => hasPayme.value && paymentSettings.scheduleChargeMode === 'save-card');

// Appointments that need the owner's approval can't be charged up front. So with manual
// approval on: Hyp can't offer "mandatory" payment (no way to defer the charge), and PayMe
// is limited to save-card (tokenize now, charge after approval). Mirrors the backend rule.
const manualApprove = computed(() => siteData.value?.configurations?.appointmentManualApprove === true);

const paymentModeOptions = computed(() => {
  const opts: Array<{ value: 'none' | 'optional' | 'force'; label: string; desc: string }> = [
    { value: 'none', label: 'ללא תשלום', desc: 'הלקוח קובע תור ללא תשלום' },
    { value: 'optional', label: 'תשלום אופציונלי', desc: 'הלקוח יכול לבחור אם לשלם (אם כן — תשלום מלא בלבד)' },
    { value: 'force', label: 'תשלום חובה', desc: 'חובה לשלם לקביעת תור (הלקוח בוחר מלא או מקדמה)' },
  ];
  return manualApprove.value && !hasPayme.value ? opts.filter(o => o.value !== 'force') : opts;
});

const chargeModeOptions = computed(() => {
  const opts: Array<{ value: 'immediate' | 'before-appointment' | 'save-card'; label: string; desc: string }> = [
    { value: 'immediate', label: 'חיוב מיידי', desc: 'הלקוח מחויב בעת קביעת התור' },
    { value: 'before-appointment', label: 'חיוב לפני התור', desc: 'פרטי הכרטיס נשמרים והחיוב מתבצע אוטומטית מספר שעות לפני התור' },
    { value: 'save-card', label: 'שמירת כרטיס לחיוב ידני', desc: 'פרטי הכרטיס נשמרים ללא חיוב; העסק מחייב מהמערכת עד גובה ההזמנה' },
  ];
  return manualApprove.value ? opts.filter(o => o.value === 'save-card') : opts;
});

const isConnected = computed(() => !!siteData.value?.hasPayments);
const providerLabel = computed(() => {
  const labels: Record<string, string> = {
    [PaymentProvider.PAYME]: 'PayMe',
    [PaymentProvider.HYP]: 'Hyp'
  };
  const provider = siteData.value?.paymentIntegration?.paymentConfig?.provider;
  return provider ? labels[provider] ?? provider : '';
});

const goToIntegrations = () => {
  trackEvent('settings_payments_connect_clicked');
  router.push({ name: AdminRouteNames.Integrations });
};

// ── Automatic invoice — the InvoiceReceipt automation, surfaced here for convenience ──
const { openDrawer } = useDrawer();
const invoiceAutomationConfig = ref<IAutomationConfig | null>(null);
const invoiceAutomationEnabled = computed(() => !!invoiceAutomationConfig.value?.enabled);
const hasPaymeInvoice = computed(() => siteData.value?.paymentIntegration?.invoiceConfig?.provider === InvoiceProvider.PAYME);
const hasWhatsApp = computed(() => !!adminStore.getBusinessSiteData?.whatsappIntegration?.accessToken);
const hasLoyalty = computed(() => !!adminStore.getBusinessSiteData?.loyaltyProgram);

const loadInvoiceAutomation = async () => {
  if (!hasPaymeInvoice.value) return;
  try {
    const configs = await CommunicationApi.fetchAutomationConfigs();
    invoiceAutomationConfig.value = configs.find(c => c.type === AutomationTypeEnum.InvoiceReceipt) ?? null;
  } catch (e) {
    console.error(e);
  }
};

const saveInvoiceAutomation = async (config: IAutomationConfig) => {
  invoiceAutomationConfig.value = await CommunicationApi.upsertAutomationConfig(config);
};

// Same flow as the Automations page: enabling opens the message editor drawer,
// disabling saves directly.
const toggleInvoiceAutomation = async (enabled: boolean) => {
  trackEvent('automation_toggled', { type: AutomationTypeEnum.InvoiceReceipt, enabled, origin: 'payments-settings' });
  const config = invoiceAutomationConfig.value ?? createDefaultConfig(AutomationTypeEnum.InvoiceReceipt);
  if (enabled) {
    const configCopy: IAutomationConfig = { ...config, enabled: true, settings: { ...config.settings } };
    const meta = automationTypesMeta[AutomationTypeEnum.InvoiceReceipt];
    openDrawer(EditAutomationDrawer, {
      config: configCopy,
      meta,
      hasWhatsApp: hasWhatsApp.value,
      hasLoyalty: hasLoyalty.value,
      hasPaymeInvoice: hasPaymeInvoice.value,
      onSave: async (updatedConfig: IAutomationConfig) => {
        await saveInvoiceAutomation(updatedConfig);
        toast.success('האוטומציה הופעלה בהצלחה');
      },
    }, { title: meta.name });
  } else {
    try {
      await saveInvoiceAutomation({ ...config, enabled: false });
    } catch (e) {
      console.error(e);
      toast.error('שמירת האוטומציה נכשלה');
    }
  }
};

const onAmountChoiceChanged = (val: 'full' | 'partial') => {
  if (val === 'full')
    paymentSettings.scheduleAdvancedPaymentPercentage = 100;
  else if (!paymentSettings.scheduleAdvancedPaymentPercentage || paymentSettings.scheduleAdvancedPaymentPercentage >= 100)
    paymentSettings.scheduleAdvancedPaymentPercentage = 50;
};

const onPaymentModeChange = (v: 'none' | 'optional' | 'force') => {
  paymentSettings.schedulePaymentMode = v;
  // Charge-later timing (save-card / before-appointment) only applies to force. Reset it when
  // leaving force so an optional payment charges immediately instead of tokenizing a stale value.
  if (v !== 'force') paymentSettings.scheduleChargeMode = 'immediate';
};

const initData = () => {
  const configs = siteData.value?.configurations;

  // Check if new payment configuration exists
  const hasNewConfig = configs?.schedulePaymentMode !== null && configs?.schedulePaymentMode !== undefined;

  const dataToAssign: TSettingsPayments = {
    businessType: configs?.businessType ?? 'patur',
    allowTashlumim: configs?.allowTashlumim ?? false,
    numOfTashlumim: configs?.numOfTashlumim ?? undefined,

    // New payment configuration
    schedulePaymentMode: hasNewConfig
      ? configs.schedulePaymentMode
      : (configs?.isScheduleRequirePayment ? 'force' : 'none'),
    scheduleAdvancedPaymentPercentage: configs?.scheduleAdvancedPaymentPercentage ?? 50,

    // Advanced payment mechanism (timing / scope / cancellation policy)
    scheduleChargeMode: configs?.scheduleChargeMode ?? 'immediate',
    scheduleChargeHoursBefore: configs?.scheduleChargeHoursBefore ?? 2,
    scheduleChargeScope: configs?.scheduleChargeScope ?? 'always',

    paymentLinkChannel: configs?.paymentLinkChannel ?? 'sms',
    invoiceLinkChannel: configs?.invoiceLinkChannel ?? 'sms',
    voucherChannel: configs?.voucherChannel ?? 'sms',
  };

  Object.assign(paymentSettings, dataToAssign);
  // Reflect the manual-approve rule up front so the shown selection matches what will be saved.
  if (manualApprove.value && paymentSettings.schedulePaymentMode === 'force') {
    if (hasPayme.value) paymentSettings.scheduleChargeMode = 'save-card';
    else paymentSettings.schedulePaymentMode = 'optional';
  }
  // Charge-later timing only applies to force — drop a stale save-card from a prior force config
  // so it isn't re-saved (and can't tokenize an optional payment). Mirrors the backend rule.
  if (paymentSettings.schedulePaymentMode !== 'force') {
    paymentSettings.scheduleChargeMode = 'immediate';
  }
  amountChoice.value = dataToAssign.scheduleAdvancedPaymentPercentage === 100 ? 'full' : 'partial';
  takeSnapshot();
};

const saveChanges = async () => {
  trackEvent('settings_payments_saved');
  try {
    if (paymentSettings.allowTashlumim && !paymentSettings.numOfTashlumim) {
      toast.error('יש לבחור מספר תשלומים רצוי :)');
      return;
    }

    // Validate new payment configuration
    // Only validate percentage for 'force' mode (optional mode doesn't use advanced payment)
    if (paymentSettings.schedulePaymentMode === 'force') {
      if (isSaveCardTiming.value || amountChoice.value === 'full') {
        // Amount is decided by the business at charge time (save-card) or full payment —
        // in both cases persist 100%.
        paymentSettings.scheduleAdvancedPaymentPercentage = 100;
      } else if (!paymentSettings.scheduleAdvancedPaymentPercentage ||
          paymentSettings.scheduleAdvancedPaymentPercentage < 1 ||
          paymentSettings.scheduleAdvancedPaymentPercentage > 99) {
        toast.error('יש להזין אחוז מקדמה בין 1 ל-99');
        return;
      }

      if (hasPayme.value && paymentSettings.scheduleChargeMode === 'before-appointment') {
        if (!paymentSettings.scheduleChargeHoursBefore ||
            paymentSettings.scheduleChargeHoursBefore < 1 ||
            paymentSettings.scheduleChargeHoursBefore > 168) {
          toast.error('יש להזין מספר שעות בין 1 ל-168');
          return;
        }
      }
    }

    loadingCta.value = true;
    // Without a clearing provider only the voucher channel is shown — don't save the hidden
    // payment-mode defaults along with it.
    await SettingsApi.updateSettingsPayment(siteData.value.hasPayments
      ? paymentSettings
      : { voucherChannel: paymentSettings.voucherChannel } as TSettingsPayments);
    flashSaved();
    await adminStore.fetchBusinessData();
    initData();
  } catch (e) {
    toast.error('לא הצלחנו לעדכן את פרטי המסוף');
  }
  loadingCta.value = false;
};

initData();
loadInvoiceAutomation();

defineExpose({ saveChanges, loadingCta, dirty });
</script>
